/**
 * Global feature flags for tool routing & rendering behaviour.
 *
 * 2026-05-10 (Miles directive, Slack C0AMX0ACS9K/1778451390.755569):
 *   "this shouldn't be a hardcoded response (never should be). we shouldn't
 *    go down all the edge cases to avoid this issue anywhere in the account
 *    specific Prest0n ever"
 *
 * The orchestrator (LLM) is the sole authority for routing decisions and
 * for composing user-visible reply text. Regex / pattern matchers are
 * informational hints to the LLM, never routing pre-empts.
 *
 * These flags are intentionally module-level constants rather than env-var
 * lookups so that:
 *   - the bundle ships with a single, deterministic value;
 *   - the CI lint (scripts/forbid-hardcoded-user-strings.py) can grep for
 *     `ORCHESTRATOR_AUTHORITATIVE_ROUTING` and confirm it landed in the
 *     deployed bundle;
 *   - a quick single-line revert is possible if a prod regression is found.
 */
/* eslint-disable */

/**
 * When true (DEFAULT), the orchestrator/LLM is the only routing authority
 * for inbound user messages. Regex tool-trigger matches are still computed
 * but become INFORMATIONAL HINTS attached to pageContext.contextHint —
 * the LLM decides whether (and how) to invoke the matched tool.
 *
 * When false (escape hatch only), the legacy regex-pre-empt routing in
 * useGlobalTools.ts is restored. Used only for emergency rollback.
 */
export const ORCHESTRATOR_AUTHORITATIVE_ROUTING: boolean = true;

/**
 * When true (DEFAULT), tool-emitted structured events flow through the
 * post-tool renderer (renderStructuredEvent) which composes user-visible
 * text via an LLM call instead of using the tool's hardcoded text/html.
 * Tools must always emit `payload:` instead of `text:`/`html:` literal
 * fields once Phase D ships.
 *
 * Today this flag wraps the renderer entry-point so we can fall back to
 * the legacy hardcoded text in the unlikely event the renderer LLM call
 * fails. Phase D removes the literal `text:` field outright.
 */
export const STRUCTURED_EVENT_RENDERING: boolean = true;

/**
 * Build a single-line trigger-hint string suitable for embedding into
 * OrchestratorPageContext.contextHint when a regex pre-empt matched. The
 * LLM reads this as informational only — it does NOT route the message
 * to the named tool unless its own analysis concurs.
 *
 * Returning `undefined` when no match keeps the existing contextHint
 * (page-level default) intact.
 */
export function buildRoutingTriggerHint(toolId: string | null | undefined): string | undefined {
  if (!toolId) return undefined;
  return (
    `An internal regex matcher flagged this turn as potentially related to ` +
    `the "${toolId}" tool. Treat this as a soft hint — your own judgement is ` +
    `authoritative. If the user is asking a broader question (e.g. an environment ` +
    `survey, capabilities check, or "what tools do you have access to") DO NOT ` +
    `invoke the matched tool; compose a comprehensive natural-language reply instead.`
  );
}

/**
 * Loose translation-intent observation as an informational hint (2026-08-24).
 *
 * Successor to the Track C pre-empt (2026-04-22 → 2026-08-24), which routed
 * "translate"-intent turns with a PDF/DOCX attachment straight to the browser
 * translate tool before any planner/orchestrator saw them. Under the master
 * rule (Primacy Audit D2) the primary orchestrator makes the routing decision;
 * the detector survives only as evidence. The hint also states the deliverable
 * contract so a translation decision produces complete downloadable files —
 * the failure Track C originally papered over (INCIDENT-20260824-C: a
 * 30-page translation answered with a 376-character inline sample).
 */
export function buildTranslateIntentHint(observation: {
  targetLanguage?: string | null;
  carriedFromHistory?: boolean;
}): string {
  const target = observation.targetLanguage ? ` (target language: ${observation.targetLanguage})` : '';
  const carried = observation.carriedFromHistory
    ? ' The translate verb appeared in the previous turn; this message carries the document — the intent spans the lead-in.'
    : '';
  return (
    `An internal matcher detected loose translation intent${target} together with a ` +
    `PDF/DOCX attachment on this turn.${carried} Treat this as a soft hint — your own ` +
    `judgement is authoritative. If this IS a document translation job, the deliverable ` +
    `contract applies: translate the ENTIRE document and return it as downloadable ` +
    `PDF/DOCX files — never an inline sample, excerpt, or a promise of files to come.`
  );
}

/**
 * Pending-question observation as an informational hint (D8 tail phase 2,
 * 2026-08-25).
 *
 * When a client workflow is mid-question ("waiting for your answer") and the
 * break-out predicate classified the user's next message as AMBIGUOUS —
 * neither an obvious field answer (those keep the fast client path, D18) nor
 * an explicit cancel — the message dispatches as a normal server turn
 * carrying this hint, the parked workflow SURVIVES client-side, and the
 * primary decides answer-vs-new-topic with the whole thread in view. The
 * [PENDING_QUESTION_HINT] marker is the server-side detection anchor
 * (runtime.ts renders it into the begin_turn observation slot); keep it
 * stable.
 */
export const PENDING_QUESTION_HINT_MARKER = '[PENDING_QUESTION_HINT]';

export function buildPendingQuestionHint(observation: {
  toolId: string;
  ambiguousMatched?: string | null;
}): string {
  const matched = observation.ambiguousMatched
    ? ` An internal matcher found the phrasing ambiguous (${observation.ambiguousMatched}).`
    : '';
  return (
    `${PENDING_QUESTION_HINT_MARKER} A client workflow ("${observation.toolId}") is mid-question — ` +
    `it asked the user something and is still waiting for the answer; the waiting form was left ` +
    `intact.${matched} Treat this as evidence, not a decision: judge with the whole thread whether ` +
    `this message answers that pending question or starts a new topic, and handle it accordingly — ` +
    `if it clearly answers the pending question, say so and invite the user to send it to the ` +
    `waiting form (it is still open); if it is a new topic, just handle the new topic.`
  );
}

/**
 * Surface-plan P2 (2026-08-29, owner decision #2 — separate-messages
 * composition): when true, the workflow-CTA liveness rule becomes
 * TURN-AWARE — a surface message stamped with the SAME turn's
 * `surfaceSeriesAnchor` (a file card or receipt delivered in series beside a
 * decision card) does not retire that card's buttons; anything else newer
 * still does, and over-retiring stays the intended failure direction.
 *
 * DEFAULT OFF: with no emitters stamping the anchor yet (they arrive with
 * the deliverables and desktop phases), on/off is behaviorally identical —
 * the flag exists so the client mechanics ship dark and flip per-account
 * first, per the rollout doctrine. Single-line revert, same as every flag
 * in this module.
 */
export const SURFACE_SERIES_COMPOSITION: boolean = false;
